/**
 * Presentation only: imported by CollectionFfveAppearance and Rater.
 * No product export, remote script, basket data or authentication is loaded here.
 * Reference: https://www.ffve.org/produit/assurance-vehicule-de-collection/
 */
export const isCollectionFfve = (productCode, brand) =>
  productCode === "collection" && brand?.theme === "ffve";

const bodyAttribute = "data-collection-ffve";

// MUI portals live under body, outside the Rater layout. Remove the scope when
// leaving this product so that the other brands retain their original styles.
export function mountCollectionFfveAppearance(body) {
  const previous = body.getAttribute(bodyAttribute);
  body.setAttribute(bodyAttribute, "true");
  return () => {
    if (previous === null) body.removeAttribute(bodyAttribute);
    else body.setAttribute(bodyAttribute, previous);
  };
}

export function getCollectionFfveBrand(productCode, brand) {
  if (!isCollectionFfve(productCode, brand)) return brand;

  const config = brand.config || {};
  return {
    ...brand,
    title: "Assurance véhicule de collection · FFVE",
    logo: {
      url: "https://www.ffve.org/wp-content/uploads/2024/09/512px-Logo-FFVE.png",
      alternativeText: "Fédération Française des Véhicules d’Époque",
    },
    icon: {
      url: "https://www.ffve.org/wp-content/uploads/2024/09/512px-Logo-FFVE-150x150.png",
    },
    config: {
      ...config,
      // Replace the legacy red stylesheet; it uses unscoped, generated classes.
      // The replacement CSS is bundled through CollectionFfveAppearance.
      css: null,
      headerLabel: "Assurance véhicule de collection",
      headerCaption: "En partenariat avec Carène Assurances",
      style: {
        ...config.style,
        HeaderLogoImg: {
          ...config.style?.HeaderLogoImg,
          img: {
            ...config.style?.HeaderLogoImg?.img,
            logo: { height: "72px", width: "72px", objectFit: "contain" },
          },
        },
      },
    },
  };
}

const bodyFont = '"Open Sans", Arial, sans-serif';
const headingFont = '"Roboto Slab", Georgia, serif';

// MUI does not read the legacy CSS variables. Supply the same palette to its
// theme, including disabled, focus, error and portal components.
export const collectionFfveThemeOptions = {
  palette: {
    primary: { main: "#0D4677", light: "#E5F2FF", dark: "#0E395E", contrastText: "#FFFFFF" },
    secondary: { main: "#0E395E", light: "#526477", dark: "#00204E", contrastText: "#FFFFFF" },
    info: { main: "#FFB400", light: "#FFC22E", dark: "#A66F00", contrastText: "#333333" },
    error: { main: "#B42318", contrastText: "#FFFFFF" },
    warning: { main: "#EA452E", contrastText: "#FFFFFF" },
    success: { main: "#24734A", contrastText: "#FFFFFF" },
    text: { primary: "#333333", secondary: "#526477" },
    background: { default: "#F4F4F4", paper: "#FFFFFF" },
  },
  typography: {
    fontFamily: bodyFont,
    h1: { fontFamily: headingFont, fontWeight: 600 },
    h2: { fontFamily: headingFont, fontWeight: 600 },
    h3: { fontFamily: headingFont, fontWeight: 700 },
    h4: { fontFamily: headingFont, fontWeight: 700 },
    h5: { fontFamily: headingFont },
    h6: { fontFamily: headingFont },
    button: { fontFamily: bodyFont, fontWeight: 600, textTransform: "none" },
  },
  components: {
    MuiTypography: { styleOverrides: { root: { color: "inherit" } } },
    MuiFormLabel: {
      styleOverrides: { root: { fontFamily: bodyFont, color: "#526477" } },
    },
    MuiButton: {
      styleOverrides: {
        root: { fontFamily: bodyFont, fontSize: "1rem", minHeight: 44 },
        containedInfo: {
          backgroundColor: "#FFB400",
          color: "#333333",
          "&:hover": { backgroundColor: "#FFC22E" },
        },
      },
    },
    MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 12 } } },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          color: "#0D4677",
          "&.Mui-selected, &.Mui-selected:hover": {
            backgroundColor: "#0D4677",
            color: "#FFFFFF",
          },
        },
      },
    },
    MuiTab: { styleOverrides: { root: { color: "#0D4677" } } },
  },
};
